import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, Pause, Play, X } from "lucide-react";

import { SlideAnnotationLayer } from "./slide-annotation-layer";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ScaledSlideIframe } from "@/components/slides/scaled-slide-iframe";
import { useSlideStore } from "@/stores/slide-store";
import type {
  PresentationConfig,
  SlideInfo,
} from "@/stores/slide-store";
import { useSlideThumbnail } from "@/hooks/use-slide-thumbnail";
import { fetchSlideManifest } from "@/lib/slides/slide-manifest";
import { previewMessage } from "@/lib/visual-editor/post-message";

interface PresenterDashboardProps {
  previewUrl: string;
  devContainerId?: string;
  projectId: string;
  getIdToken: () => Promise<string | null>;
  onClose: () => void;
}

// Cross-tab sync between the presenter window (Kaze origin) and the editor
// tab (Kaze origin). Both same-origin so BroadcastChannel is the right tool.
// Scope by projectId so multi-project tabs don't cross-sync.
const CHANNEL_NAME = (projectId: string) =>
  `optidev-slide-sync:${projectId}`;

// Refresh interval for the slide manifest — picks up newly created /
// renamed / deleted slides from the editor without needing a socket
// connection in the popup window. 5s is responsive enough that new slides
// appear within a typical user attention span, light enough that a 30-min
// presentation only fires ~360 small fetches.
const MANIFEST_POLL_MS = 5000;

export type SlideSyncMsg =
  | { type: "slide-navigate"; slideIndex: number }
  | { type: "end-presentation" }
  | {
      type: "presentation-config-update";
      config: Partial<PresentationConfig>;
    }
  // Annotation sync — see components/slides/annotation-overlay.tsx and
  // 01-plan.md Task 2.1. Full-SVG-snapshot model: each stroke commit
  // posts the slide's complete SVG. BC is same-origin so bandwidth is
  // free; commits fire at most per stroke-end (not per pointer move).
  | { type: "annotation-update"; slideIndex: number; svg: string }
  | { type: "annotation-clear"; slideIndex: number };

type Msg = SlideSyncMsg;

export function PresenterDashboard({
  previewUrl,
  devContainerId,
  projectId,
  getIdToken,
  onClose,
}: PresenterDashboardProps) {
  const slides = useSlideStore((s) => s.slides);
  const currentSlideIndex = useSlideStore((s) => s.currentSlideIndex);
  const totalSlides = useSlideStore((s) => s.totalSlides);
  const autoplay = useSlideStore((s) => s.presentationConfig.autoplay);
  const intervalMs = useSlideStore((s) => s.presentationConfig.interval);
  const loop = useSlideStore((s) => s.presentationConfig.loop);
  const aspectRatio = useSlideStore(
    (s) => s.presentationConfig.aspectRatio,
  );

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Snapshot of the deck's authored `autoplay` from slides.json, captured
  // on the first manifest load. The live `autoplay` flag in the store is
  // session-controlled (toggled by ▶/⏸), so we can't gate UI on it
  // directly — once the user pauses, gating on `autoplay` would also hide
  // the button. This snapshot stays fixed for the session and represents
  // "does the deck author intend autoplay?" — which is the right basis
  // for showing the toggle at all.
  const [deckAutoplay, setDeckAutoplay] = useState<boolean | null>(null);
  const bcRef = useRef<BroadcastChannel | null>(null);
  // Ref to the LEFT mirror iframe — used to dispatch `slide-navigate`
  // postMessage so the template's SlideRenderer handles transitions
  // internally (no reload, no flicker, animations preserved).
  const mirrorIframeRef = useRef<HTMLIFrameElement | null>(null);

  // Filmstrip drag-to-scroll — same pattern as `theme-preview-card.tsx`'s
  // library row. `hasDraggedRef` gates the thumbnail click so a release
  // after a real drag (> 3px movement) doesn't also trigger navigation.
  const filmstripRef = useRef<HTMLDivElement | null>(null);
  const [isFilmstripDragging, setIsFilmstripDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0 });
  const hasDraggedRef = useRef(false);

  const handleFilmstripMouseDown = useCallback((e: React.MouseEvent) => {
    const container = filmstripRef.current;
    if (!container) return;
    setIsFilmstripDragging(true);
    hasDraggedRef.current = false;
    dragStartRef.current = {
      x: e.pageX,
      scrollLeft: container.scrollLeft,
    };
  }, []);

  const handleFilmstripMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isFilmstripDragging) return;
      const container = filmstripRef.current;
      if (!container) return;
      e.preventDefault();
      const dx = e.pageX - dragStartRef.current.x;
      if (Math.abs(dx) > 3) hasDraggedRef.current = true;
      container.scrollLeft = dragStartRef.current.scrollLeft - dx;
    },
    [isFilmstripDragging],
  );

  const handleFilmstripMouseUp = useCallback(
    () => setIsFilmstripDragging(false),
    [],
  );
  const handleFilmstripMouseLeave = useCallback(
    () => setIsFilmstripDragging(false),
    [],
  );

  // Initial src for the iframe — captured ONCE on mount so subsequent
  // navigation is purely via postMessage. Falls back to a string template
  // if previewUrl isn't a valid URL. `autoplay=0` forces the template's
  // own autoplay engine off (it parses URL params and they win over
  // slides.json config) — Kaze drives autoplay below via setTimeout +
  // slide-navigate postMessage, which is the only way to reliably
  // pause/resume mid-presentation because the template's `isAutoPlaying`
  // state isn't reactive to config updates.
  const initialIframeSrc = useMemo(() => {
    const slideIndex = useSlideStore.getState().currentSlideIndex;
    try {
      const u = new URL(previewUrl);
      u.searchParams.set("slide", String(slideIndex));
      u.searchParams.set("presenter", "1");
      u.searchParams.set("autoplay", "0");
      return u.toString();
    } catch {
      const sep = previewUrl.includes("?") ? "&" : "?";
      return `${previewUrl}${sep}slide=${slideIndex}&presenter=1&autoplay=0`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  // Elapsed timer
  useEffect(() => {
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Snapshot the deck's authored autoplay once slides are populated. The
  // manifest is fetched async by PresenterRoute, so on first dashboard
  // render `slides` may still be empty and `autoplay` the store default —
  // capturing in a useState initializer would lock in the wrong value.
  useEffect(() => {
    if (deckAutoplay !== null) return;
    if (totalSlides === 0) return;
    setDeckAutoplay(
      useSlideStore.getState().presentationConfig.autoplay ?? false,
    );
  }, [deckAutoplay, totalSlides]);

  // BroadcastChannel setup. Inbound:
  // - `presentation-config-update` from the editor's presentation-settings
  //   gear popover (so config edits propagate live; we skip the 5s
  //   manifest poll for these fields to keep the live ▶/⏸ state from
  //   getting clobbered).
  // BC does NOT echo to the sender, so our own ▶/⏸ broadcasts won't
  // loop back here.
  useEffect(() => {
    const bc = new BroadcastChannel(CHANNEL_NAME(projectId));
    bcRef.current = bc;
    bc.onmessage = (e: MessageEvent<Msg>) => {
      if (!e.data || typeof e.data !== "object") return;
      if (
        e.data.type === "presentation-config-update" &&
        e.data.config &&
        typeof e.data.config === "object"
      ) {
        useSlideStore.getState().setPresentationConfig(e.data.config);
        // If the editor explicitly changed the deck's authored autoplay,
        // update the snapshot too so the ▶/⏸ button appears/disappears
        // to match. Without this, enabling autoplay after the presenter
        // opens leaves the toggle hidden.
        if (typeof e.data.config.autoplay === "boolean") {
          setDeckAutoplay(e.data.config.autoplay);
        }
        // Forward to the LEFT mirror iframe so the template's own state
        // (transition, etc.) tracks the new config too.
        mirrorIframeRef.current?.contentWindow?.postMessage(e.data, "*");
      }
      // Annotation messages are handled by useSlideAnnotations (its
      // own BC channel on the same name). Multiple BC channels of the
      // same name receive in parallel; the hook is the single owner
      // of annotation handling.
    };
    return () => {
      bc.close();
      bcRef.current = null;
    };
  }, [projectId]);

  // Poll the manifest so new slides added in the editor appear here. Also
  // bumps thumbnailRevision after each refresh so the filmstrip re-fetches
  // any updated JPEGs. `syncPresentationConfig: false` — autoplay /
  // interval / loop are session-controlled here (▶/⏸ button + scheduler
  // below); letting the manifest's `autoplay: false` default write back
  // every 5s would kill the live scheduler mid-presentation.
  useEffect(() => {
    if (!devContainerId) return;
    const tick = async () => {
      await fetchSlideManifest({
        devContainerId,
        getIdToken,
        syncPresentationConfig: false,
      });
      useSlideStore.getState().bumpThumbnailRevision();
    };
    const id = setInterval(tick, MANIFEST_POLL_MS);
    return () => clearInterval(id);
  }, [devContainerId, getIdToken]);

  const navigateTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSlides) return;
      useSlideStore.getState().setCurrentSlide(index);
      // 1. Drive the LEFT mirror iframe via postMessage so the template's
      //    SlideRenderer handles the transition internally — no iframe
      //    reload, smooth animations, faster perceived nav. Same helper
      //    the editor's preview iframe uses elsewhere in the codebase.
      previewMessage.navigateToSlide(mirrorIframeRef.current, index);
      // 2. Broadcast to the editor tab + audience overlay over the same
      //    channel so they stay in sync.
      const msg: Msg = { type: "slide-navigate", slideIndex: index };
      bcRef.current?.postMessage(msg);
    },
    [totalSlides],
  );

  const goNext = useCallback(
    () => navigateTo(currentSlideIndex + 1),
    [currentSlideIndex, navigateTo],
  );
  const goPrev = useCallback(
    () => navigateTo(currentSlideIndex - 1),
    [currentSlideIndex, navigateTo],
  );

  const handleEnd = useCallback(() => {
    const msg: Msg = { type: "end-presentation" };
    bcRef.current?.postMessage(msg);
    onClose();
  }, [onClose]);

  // Live autoplay toggle — broadcasts to the editor tab (which relays into
  // the in-page Present overlay) and posts directly to this window's own
  // LEFT mirror iframe. NOT persisted to slides.json — `autoplay` in the
  // manifest is the deck's default, this is just session state.
  const togglePlay = useCallback(() => {
    const next = !useSlideStore.getState().presentationConfig.autoplay;
    useSlideStore.getState().setPresentationConfig({ autoplay: next });
    const msg: Msg = {
      type: "presentation-config-update",
      config: { autoplay: next },
    };
    bcRef.current?.postMessage(msg);
    mirrorIframeRef.current?.contentWindow?.postMessage(msg, "*");
  }, []);

  // Annotation feature lives entirely inside <SlideAnnotationLayer />
  // below — BC channel, drauu overlay, toolbar, lifecycle.

  // Kaze-owned autoplay scheduler. Fires a single setTimeout per slide;
  // dep on `currentSlideIndex` reschedules whenever the slide changes
  // (manual nav OR autoplay tick), so the user always gets a full
  // `interval` ms on the new slide. Template's native autoplay is
  // disabled via `?autoplay=0` in the iframe src above — Kaze is the only
  // driver, which is the only way to reliably pause mid-presentation
  // because the template's internal `isAutoPlaying` state doesn't react
  // to `presentation-config-update` postMessages.
  useEffect(() => {
    if (!autoplay || totalSlides <= 1) return;
    const id = setTimeout(() => {
      const next = useSlideStore.getState().currentSlideIndex + 1;
      if (next < totalSlides) {
        navigateTo(next);
      } else if (loop) {
        navigateTo(0);
      } else {
        // End of deck, no loop — auto-stop.
        useSlideStore
          .getState()
          .setPresentationConfig({ autoplay: false });
        const stopMsg: Msg = {
          type: "presentation-config-update",
          config: { autoplay: false },
        };
        bcRef.current?.postMessage(stopMsg);
        mirrorIframeRef.current?.contentWindow?.postMessage(stopMsg, "*");
      }
    }, intervalMs);
    return () => clearTimeout(id);
  }, [autoplay, currentSlideIndex, intervalMs, totalSlides, loop, navigateTo]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Escape") {
        onClose();
      } else if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, onClose, togglePlay]);

  const currentSlide = slides[currentSlideIndex];

  const formatTime = (s: number): string => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m.toString().padStart(2, "0")}:${r.toString().padStart(2, "0")}`;
  };

  const wallClock = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    // Light palette — clean white panels on a soft slate-50 base, matches the
    // Replit reference (Image #20). Reads as polished + presentation-ready
    // and keeps the focus on the slide content (which usually has its own
    // colors).
    <div className="fixed inset-0 z-50 flex flex-col text-slate-900 bg-slate-50">
      {/* Main content */}
      <div className="flex-1 flex min-h-0 p-5 gap-5">
        {/* LEFT 60% — live mirror */}
        <div className="w-[60%] flex flex-col min-w-0">
          <div className="flex-1 relative bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm shadow-slate-900/5">
            <ScaledSlideIframe
              ref={mirrorIframeRef}
              src={initialIframeSrc}
              aspectRatio={aspectRatio === "4:3" ? "4:3" : "16:9"}
              title="Slide preview"
              allow="fullscreen"
              sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
            />
            <button
              onClick={goPrev}
              disabled={currentSlideIndex === 0}
              aria-label="Previous slide"
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 backdrop-blur shadow-md shadow-slate-900/10 ring-1 ring-slate-200 hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <button
              onClick={goNext}
              disabled={currentSlideIndex >= totalSlides - 1}
              aria-label="Next slide"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 backdrop-blur shadow-md shadow-slate-900/10 ring-1 ring-slate-200 hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>

            {/* Pen annotation feature — drop-in. Renders the toolbar
                (collapsed Pen button → full pill when active) AND lazily
                mounts the drauu overlay on first tool click. BC bridge,
                lifecycle, and per-slide state all owned internally. */}
            <SlideAnnotationLayer
              iframeRef={mirrorIframeRef}
              projectId={projectId}
              role="presenter"
              toolbarClassName="absolute top-3 right-3 z-20"
              onPrev={goPrev}
              onNext={goNext}
              canPrev={currentSlideIndex > 0}
              canNext={currentSlideIndex < totalSlides - 1}
            />
          </div>
        </div>

        {/* RIGHT 40% — Notes (header INSIDE the box) */}
        <div className="w-[40%] flex flex-col min-h-0">
          <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm shadow-slate-900/5 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold text-slate-900 tracking-tight">
              Notes
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4">
                <SpeakerNotes slide={currentSlide} />
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {/* Bottom bar — flex-shrink-0 so it never shrinks under flex pressure
          and clips the filmstrip thumbnails. */}
      <div className="flex-shrink-0 flex flex-col gap-3 px-5 py-3 bg-white/80 backdrop-blur border-t border-slate-200">
        {/* Native overflow — shadcn ScrollArea's viewport sizing was clipping
            the thumbnails' bottom edges. Symmetric `py-1.5` reserves room
            both above and below for the current-slide ring (which extends
            2px outside the button on every side) + its drop shadow. The
            `-mx-1 px-1` does the same for the left/right edges.
            Mouse-drag to scroll (matches theme-preview-card.tsx). */}
        <div
          ref={filmstripRef}
          onMouseDown={handleFilmstripMouseDown}
          onMouseMove={handleFilmstripMouseMove}
          onMouseUp={handleFilmstripMouseUp}
          onMouseLeave={handleFilmstripMouseLeave}
          className={cn(
            "w-full overflow-x-auto overflow-y-hidden py-1.5 -mx-1 px-1 select-none",
            isFilmstripDragging ? "cursor-grabbing" : "cursor-grab",
          )}
        >
          <div className="flex items-center gap-2 min-w-min">
            {slides.map((slide, idx) => (
              <FilmstripThumb
                key={slide.id}
                slide={slide}
                index={idx}
                isCurrent={idx === currentSlideIndex}
                devContainerId={devContainerId}
                getIdToken={getIdToken}
                onClick={() => {
                  if (hasDraggedRef.current) return;
                  navigateTo(idx);
                }}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-[160px]">
            <div className="text-sm font-medium text-slate-700 tabular-nums">
              {currentSlideIndex + 1} / {totalSlides}
            </div>
            {/* Live ▶/⏸ — only shown when the deck is authored with
                autoplay (slides.json `autoplay: true`). For manual-advance
                decks the toggle is hidden to keep the chrome focused on
                what the deck actually supports. The deck's saved
                `autoplay` default is writable via the slide-panel gear
                popover; this button only controls session state and
                does not persist. */}
            {deckAutoplay && (
              <button
                onClick={togglePlay}
                title={
                  autoplay
                    ? "Pause autoplay (P)"
                    : `Play autoplay (P) — ${Math.round(intervalMs / 1000)}s per slide`
                }
                aria-label={autoplay ? "Pause autoplay" : "Play autoplay"}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                  autoplay
                    ? "bg-primary/10 text-primary hover:bg-primary/15"
                    : "text-slate-600 hover:bg-slate-100",
                )}
              >
                {autoplay ? (
                  <Pause className="w-3.5 h-3.5" />
                ) : (
                  <Play className="w-3.5 h-3.5" />
                )}
                <span className="tabular-nums">
                  {Math.round(intervalMs / 1000)}s
                </span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="font-mono tabular-nums">
                {formatTime(elapsedSeconds)}
              </span>
            </div>
            <span className="text-slate-400 tabular-nums">{wallClock}</span>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleEnd}
            className="gap-1.5"
          >
            <X className="w-4 h-4" />
            End Presentation
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilmstripThumb({
  slide,
  index,
  isCurrent,
  devContainerId,
  getIdToken,
  onClick,
}: {
  slide: SlideInfo;
  index: number;
  isCurrent: boolean;
  devContainerId: string | undefined;
  getIdToken: () => Promise<string | null>;
  onClick: () => void;
}) {
  const revision = useSlideStore((s) => s.thumbnailRevision);
  const { url } = useSlideThumbnail(
    slide.id,
    devContainerId,
    getIdToken,
    revision,
  );

  return (
    <button
      onClick={onClick}
      aria-label={`Go to slide ${index + 1}`}
      className={cn(
        "relative shrink-0 w-32 aspect-video rounded-md overflow-hidden bg-slate-100 transition-all",
        isCurrent
          ? "ring-2 ring-primary shadow-md shadow-slate-900/20"
          : "ring-1 ring-slate-200 opacity-90 hover:opacity-100 hover:ring-slate-300",
      )}
    >
      {url ? (
        <img
          src={url}
          alt={`Slide ${index + 1}: ${slide.title}`}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        // Loading placeholder — left-to-right shimmer sweep, same pattern as
        // slides-panel (`animate-shimmer-sweep` from tailwind.config.js).
        // Alphas tuned higher than slides-panel's because we're sweeping on a
        // light bg without the shadcn `--foreground` HSL trick — raw slate
        // alphas need more weight to read at this opacity.
        <div className="relative w-full h-full overflow-hidden bg-slate-200">
          <div className="absolute inset-y-0 -inset-x-1/4 animate-shimmer-sweep bg-[linear-gradient(90deg,transparent_0%,rgba(15,23,42,0.10)_30%,rgba(15,23,42,0.28)_50%,rgba(15,23,42,0.10)_70%,transparent_100%)] will-change-transform" />
        </div>
      )}
      <span className="absolute bottom-1 left-1 px-1.5 rounded bg-white/85 backdrop-blur text-[10px] font-medium text-slate-700 tabular-nums">
        {index + 1}
      </span>
    </button>
  );
}

function SpeakerNotes({ slide }: { slide: SlideInfo | undefined }) {
  if (!slide?.speakerNotes) {
    return (
      <p className="text-sm text-slate-400 italic">No notes for this slide</p>
    );
  }
  return (
    // `whitespace-pre-wrap` preserves user line breaks;
    // `[overflow-wrap:anywhere]` force-breaks long unbroken strings (e.g.
    // a pasted token with no spaces) instead of letting them blow past
    // the panel width. (`anywhere` is stricter than `break-words`, so we
    // only need the one rule.)
    <div className="text-sm text-slate-700 whitespace-pre-wrap [overflow-wrap:anywhere] leading-relaxed">
      {slide.speakerNotes}
    </div>
  );
}
